import {ReactNode} from 'react';
import {
  IconButton,
  Avatar,
  Box,
  CloseButton,
  Flex,
  HStack,
  VStack,
  Icon,
  useColorMode,
  useColorModeValue,
  Link,
  Drawer,
  DrawerContent,
  Text,
  useDisclosure,
  BoxProps,
  FlexProps,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Button,
  WrapItem
} from '@chakra-ui/react';
import {
  FiHome,
  FiTrendingUp,
  FiCompass,
  FiStar,
  FiSettings,
  FiUsers,
  FiMenu,
  FiBell,
  FiChevronDown,
  FiBookOpen
} from 'react-icons/fi';
import {SunIcon, MoonIcon} from '@chakra-ui/icons';
import {IoIosHelpBuoy} from 'react-icons/io';
import {IconType} from 'react-icons';
import {ReactText} from 'react';
import {Logo} from '../../components/AppComponents/Logo';
import {Link as RouterLink} from 'react-router-dom';

interface LinkItemProps {
  name: string;
  icon: IconType;
}
const LinkItems: Array<LinkItemProps> = [
  {name: 'Übersicht', icon: FiHome},
  {name: 'Inhalte', icon: FiBookOpen},
  {name: 'Klassen', icon: FiUsers},
  {name: 'Hilfe', icon: IoIosHelpBuoy}
];

interface SidebarProps extends BoxProps {
  onClose: () => void;
}

const SidebarContent = ({onClose, ...rest}: SidebarProps) => {
  const {colorMode, toggleColorMode} = useColorMode();
  return (
    <Flex
      transition='3s ease'
      bg={useColorModeValue('white', 'whiteAlpha.50')}
      borderRight='1px'
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={{base: 'full', md: 60}}
      pos='fixed'
      h='full'
      {...rest}
    >
      <Flex h='20' alignItems='center' mx='6' justifyContent='space-between'>
        <Logo fill={useColorModeValue('#666', '#fff')} />
        <CloseButton display={{base: 'flex', md: 'none'}} onClick={onClose} />
      </Flex>
      <Flex as='nav' gap={2} flex={1} flexDirection='column'>
        {LinkItems.map(link => (
          <NavItem key={link.name} icon={link.icon}>
            {link.name}
          </NavItem>
        ))}
      </Flex>
      <Flex p='4'>
        <Button variant='ghost' onClick={toggleColorMode}>
          {colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
        </Button>
      </Flex>
    </Flex>
  );
};

interface NavItemProps extends FlexProps {
  icon: IconType;
  children: ReactText;
  isActive?: boolean;
}
const NavItem = ({icon, isActive, children, ...rest}: NavItemProps) => {
  return (
    <Link
      to='#'
      as={RouterLink}
      style={{textDecoration: 'none'}}
      _focus={{boxShadow: 'none'}}
    >
      <Flex
        align='center'
        gap='4'
        py='3'
        px='4'
        mx='6'
        borderRadius='lg'
        role='group'
        transition='background-color .25s ease-in-out'
        cursor='pointer'
        _hover={{
          bg: useColorModeValue('gray.100', 'whiteAlpha.200')
        }}
        {...rest}
      >
        {icon && <Icon fontSize='18' as={icon} />}
        {children}
      </Flex>
    </Link>
  );
};

interface MobileProps extends FlexProps {
  onOpen: () => void;
  isOpen?: boolean;
}
const MobileNav = ({onOpen, isOpen, ...rest}: MobileProps) => {
  return (
    <Flex
      ml={{base: 0, md: 60}}
      p={2}
      alignItems='center'
      bg={useColorModeValue('white', 'gray.900')}
      borderBottomWidth='1px'
      borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
      justifyContent={{base: 'space-between', md: 'flex-end'}}
      flexDir={isOpen ? 'column' : 'row'}
      {...rest}
    >
      <IconButton
        display={{base: 'flex', md: 'none'}}
        onClick={onOpen}
        variant='outline'
        aria-label='open menu'
        icon={<FiMenu />}
      />

      <HStack spacing={{base: '0', md: '6'}}>
        <IconButton
          size='lg'
          variant='ghost'
          aria-label='open menu'
          icon={<FiBell />}
        />
        <Flex alignItems={'center'}>
          <Menu>
            <MenuButton transition='all 0.3s' _focus={{boxShadow: 'none'}}>
              <HStack>
                <Avatar
                  size={'sm'}
                  src={
                    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80'
                  }
                />

                <Box display={{base: 'none', md: 'flex'}}>
                  <FiChevronDown />
                </Box>
              </HStack>
            </MenuButton>
            <MenuList
              bg={useColorModeValue('white', 'gray.900')}
              borderColor={useColorModeValue('gray.200', 'gray.700')}
            >
              <WrapItem p={3}>
                <Text fontSize='sm'>Maja Mustermann</Text>
              </WrapItem>

              <MenuItem>Profile</MenuItem>
              <MenuItem>Settings</MenuItem>
              <MenuItem>Billing</MenuItem>
              <MenuDivider />
              <WrapItem p={2}>
                <Button colorScheme='blue' flex={1}>
                  Sign out
                </Button>
              </WrapItem>
            </MenuList>
          </Menu>
        </Flex>
      </HStack>
    </Flex>
  );
};

export const TemplatePage = ({children}: {children: ReactNode}) => {
  const {isOpen, onOpen, onClose} = useDisclosure();

  return (
    <Box minH='100vh' bg={useColorModeValue('gray.50', 'gray.900')}>
      <SidebarContent
        as='aside'
        onClose={() => onClose}
        display={{base: 'none', md: 'flex'}}
        gap={4}
        boxShadow={useColorModeValue(
          '1px 0px 15px #E8E8E8',
          '1px 0px 15px #131212'
        )}
      />
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement='left'
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size='full'
      >
        <DrawerContent>
          <SidebarContent onClose={onClose} />
        </DrawerContent>
      </Drawer>
      {/* mobilenav */}
      <MobileNav onOpen={onOpen} isOpen={isOpen} />
      <Box ml={{base: 0, md: 60}} p='4'>
        {children}
      </Box>
    </Box>
  );
};
